"use client";

import { StaticImageData } from "next/image";
import { ReactNode, useCallback, useRef } from "react";

import { QuizCTA } from "~/components/features/quiz";
import Carousel from "~/componentsDS/Carousel";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import Reveal from "~/componentsDS/Reveal";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { CarouselApi, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { cn } from "~/lib/utils";
import { getTextFromNode } from "~/utils/getTextFromNode";

import { notAloneData } from "../data";

const textCn = "text-center text-primary500";
const ctaText = "take the 2-min quiz";

export type NotAloneItem = {
    id: string;
    title: ReactNode;
    titleClassName?: string;
    imageDesktopUrl?: string | StaticImageData;
    alt?: string;
    component?: ReactNode;
};

type NotAloneSectionProps = {
    notAloneDataProps?: NotAloneItem[];
    classNames?: string
};

const NotAloneSection = ({notAloneDataProps=notAloneData, classNames}:NotAloneSectionProps) => {
    const sendEvent = useTrackEvent();
    const prevIndexRef = useRef<number | null>(null);

    const handleCarouselSelect = useCallback(
        (api: CarouselApi) => {
            if (!api) return;

            const index = api.selectedScrollSnap();
            if (prevIndexRef.current === index) return;

            const item = notAloneDataProps[index];
            prevIndexRef.current = index;

            if (!item) return;

            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                    widget: "not_alone",
                    meta_data: {
                        image_title: getTextFromNode(item.title),
                        image_count: index + 1,
                    },
                },
            });
        },
        [sendEvent, notAloneDataProps]
    );

    return (
        <section className={cn("py-16 md:py-[96px] overflow-hidden",classNames)}>
            <ElementorContainer innerClassName="flex flex-col">
                <Reveal>
                    <Heading
                        as="h2"
                        size="sm"
                        sizes={{ md: "md" }}
                        className={cn(textCn, "max-w-[300px] mx-auto md:max-w-content mb-4")}
                    >
                        You’re not managing this{" "}
                        <Heading
                            as="i"
                            font="featureDisplay"
                            weight="400"
                            className="text-[28px] md:text-[36px]"
                        >
                            alone.
                        </Heading>
                    </Heading>
                    <Paragraph size="lg" className={cn(textCn, "mb-6 md:mb-0")}>Every prescription is reviewed and monitored by a licensed clinician.</Paragraph>
                    <Paragraph size="lg" weight="500" className={textCn}>Hormones change. Your care adapts.</Paragraph>
                </Reveal>

                <div className="relative">
                    <Carousel
                        loop={false}
                        variant="compact"
                        compactPaginationAlign="right"
                        compactPaginationBg="bg-trueBlack/10 backdrop-blur-[25px]"
                        compactPaginationDotColor="bg-gray100"
                        opts={{
                            align: "start",
                        }}
                        onSelect={handleCarouselSelect}
                        className="w-full"
                    >
                        <CarouselContentVisible className="max-lg:-ml-2">
                            {notAloneDataProps.map(({ component }, id) => (
                                <CarouselItem key={id} className="basis-[99%] md:basis-1/2 xl:basis-1/3 max-lg:pl-2">
                                    {component}
                                </CarouselItem>
                            ))}
                        </CarouselContentVisible>
                    </Carousel>
                </div>
                
                <div className="hidden md:block self-center mt-2">
                    <QuizCTA
                        variant="primary"
                        eventData={{
                            event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                            widget: "not_alone",
                            meta_data: {
                                click_text: ctaText,
                            },
                        }}
                        href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                        size="md"
                        className="max-md:w-full"
                    >
                        {ctaText}
                    </QuizCTA>
                </div>
            </ElementorContainer>
        </section>
    )
};

export default NotAloneSection;
